export type FieldOperator =
  | "eq"
  | "not_eq"
  | "in"
  | "not_in"
  | "is_true"
  | "is_false"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "between"
  | "within_last_days"
  | "older_than_days";

export type FieldDataType = "enum" | "compound_status" | "boolean" | "number" | "date" | "text";

export interface EnumOption {
  value: string;
  label: string;
}

export interface FieldDefinition {
  key: string;
  label: string;
  description: string;
  dataType: FieldDataType;
  operators: FieldOperator[];
  enumOptions?: EnumOption[];
  unit?: string;
}

export interface ConditionLeaf {
  field: string;
  operator: FieldOperator;
  value: unknown;
}

export interface ConditionTree {
  operator: "and" | "or";
  conditions: ConditionLeaf[];
}

const OPERATOR_LABELS: Record<FieldOperator, string> = {
  eq: "is",
  not_eq: "is not",
  in: "is one of",
  not_in: "is not one of",
  is_true: "is Yes",
  is_false: "is No",
  gt: "is after",
  lt: "is before",
  gte: "is on or after",
  lte: "is on or before",
  between: "is between",
  within_last_days: "within the last",
  older_than_days: "older than",
};

function formatValue(field: FieldDefinition | undefined, operator: FieldOperator, value: unknown): string {
  if (operator === "is_true" || operator === "is_false") return "";

  if (value === null || value === undefined || value === "") return "…";

  if (field?.dataType === "enum" || field?.dataType === "compound_status") {
    if (Array.isArray(value)) {
      if (value.length === 0) return "…";
      const labels = value.map((v) => {
        const opt = field.enumOptions?.find((o) => o.value === v);
        return opt?.label ?? String(v);
      });
      return labels.join(", ");
    }
    const opt = field?.enumOptions?.find((o) => o.value === value);
    return opt?.label ?? String(value);
  }

  if (field?.dataType === "number") {
    if (operator === "between" && typeof value === "object" && value !== null) {
      const { min, max } = value as { min?: number; max?: number };
      if (min !== undefined && max !== undefined) {
        return `${min} and ${max}${field.unit ? ` ${field.unit}` : ""}`;
      }
    }
    return `${value}${field?.unit ? ` ${field.unit}` : ""}`;
  }

  if (field?.dataType === "text" && Array.isArray(value)) {
    return value.join(", ");
  }

  if (operator === "within_last_days" || operator === "older_than_days") {
    return `${value} days`;
  }

  return String(value);
}

export function buildConditionDescription(
  conditions: ConditionLeaf[],
  fields: FieldDefinition[]
): string {
  if (!conditions || conditions.length === 0) return "";

  const fieldMap = new Map(fields.map((f) => [f.key, f]));

  const parts = conditions
    .filter((c) => c.field && c.operator)
    .map((c) => {
      const field = fieldMap.get(c.field);
      const fieldLabel = field?.label ?? c.field;
      const opLabel = OPERATOR_LABELS[c.operator] ?? c.operator;

      if (c.operator === "is_true") return `${fieldLabel} is Yes`;
      if (c.operator === "is_false") return `${fieldLabel} is No`;

      const valStr = formatValue(field, c.operator, c.value);
      if (!valStr || valStr === "…") return `${fieldLabel} ${opLabel} …`;
      return `${fieldLabel} ${opLabel} ${valStr}`;
    });

  return parts.join(" AND ");
}

// ─── Glossary-powered chart narrative ────────────────────────────────────────

export type GlossaryLookup = Map<string, Map<string, string>>;

interface GlossaryApiChild {
  value: string;
  spokenForm?: string | null;
  definition?: string | null;
}

interface GlossaryApiTerm {
  category: string;
  children?: GlossaryApiChild[];
}

export function buildGlossaryLookup(terms: GlossaryApiTerm[]): GlossaryLookup {
  const lookup = new Map<string, Map<string, string>>();
  for (const term of terms) {
    const cat = term.category;
    if (!lookup.has(cat)) lookup.set(cat, new Map());
    const catMap = lookup.get(cat)!;
    for (const child of term.children ?? []) {
      const label = child.spokenForm || child.definition || child.value;
      catMap.set(child.value, label);
      catMap.set(child.value.toUpperCase(), label);
      catMap.set(child.value.toLowerCase(), label);
    }
  }
  return lookup;
}

const FIELD_TO_GLOSSARY_CATEGORY: Record<string, string> = {
  location: "location",
  kennel_status: "status",
  kennel_status_norm: "status",
  adoption_level: "adoption_level",
  adoption_level_norm: "adoption_level",
  evaluation_status: "eval_status",
  behavior_score_band: "score_band",
};

function resolveGlossaryLabel(field: string, value: string, lookup: GlossaryLookup): string {
  const cat = FIELD_TO_GLOSSARY_CATEGORY[field];
  if (!cat) return value;
  const catMap = lookup.get(cat);
  return catMap?.get(value) ?? catMap?.get(value.toUpperCase()) ?? catMap?.get(value.toLowerCase()) ?? value;
}

function describeValues(field: string, value: unknown, lookup: GlossaryLookup): string {
  const vals = Array.isArray(value) ? (value as string[]) : [String(value)];
  const labels = vals.map((v) => resolveGlossaryLabel(field, v, lookup));
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  return labels.slice(0, -1).join(", ") + " or " + labels[labels.length - 1];
}

function buildConditionPhrase(leaf: ConditionLeaf, lookup: GlossaryLookup): string | null {
  const { field, operator, value } = leaf;

  switch (field) {
    case "species":
      return null;

    case "location":
      if (operator === "eq" || operator === "in") return `at ${describeValues(field, value, lookup)}`;
      if (operator === "not_eq" || operator === "not_in") return `not at ${describeValues(field, value, lookup)}`;
      return null;

    case "kennel_status":
    case "kennel_status_norm":
      if (operator === "eq" || operator === "in") return `in ${describeValues(field, value, lookup)} status`;
      if (operator === "not_eq" || operator === "not_in") return `not in ${describeValues(field, value, lookup)} status`;
      return null;

    case "adoption_level":
    case "adoption_level_norm":
      if (operator === "eq" || operator === "in") return `at ${describeValues(field, value, lookup)}`;
      return null;

    case "evaluation_status":
      if (operator === "eq" || operator === "in") {
        return `with ${describeValues(field, value, lookup)} evaluation`;
      }
      return null;

    case "behavior_score_band":
      if (operator === "eq" || operator === "in") {
        return `with a ${describeValues(field, value, lookup)} behavior score`;
      }
      return null;

    case "outcome_date":
      if (operator === "within_last_days") return `outcomed within the last ${value} days`;
      if (operator === "older_than_days") return `with an outcome more than ${value} days ago`;
      return null;

    case "intake_date":
      if (operator === "within_last_days") return `admitted within the last ${value} days`;
      if (operator === "older_than_days") return `in shelter for more than ${value} days`;
      return null;

    case "is_on_hold":
      if (operator === "is_true") return "on hold";
      if (operator === "is_false") return "not on hold";
      return null;

    case "has_medical_notes":
      if (operator === "is_true") return "with medical notes";
      if (operator === "is_false") return "without medical notes";
      return null;

    case "is_current_resident":
      if (operator === "is_true") return "currently residing in shelter";
      return null;

    case "is_struggling_now":
      if (operator === "is_true") return "flagged as struggling";
      return null;

    case "has_worsening_trend":
      if (operator === "is_true") return "with a worsening behavior trend";
      return null;

    case "has_behavior_alert":
      if (operator === "is_true") return "with an active behavior alert";
      return null;

    case "age": {
      if (operator === "gt") return `older than ${value} months`;
      if (operator === "lt") return `younger than ${value} months`;
      if (operator === "gte") return `at least ${value} months old`;
      if (operator === "lte") return `up to ${value} months old`;
      if (operator === "between") {
        if (typeof value === "object" && value !== null && "min" in value) {
          const { min, max } = value as { min: number; max: number };
          return `aged ${min} to ${max} months`;
        }
        if (Array.isArray(value)) return `aged ${value[0]} to ${value[1]} months`;
      }
      return null;
    }

    case "length_of_stay": {
      if (operator === "gt") return `in shelter for more than ${value} days`;
      if (operator === "lt") return `in shelter for fewer than ${value} days`;
      if (operator === "gte") return `in shelter for at least ${value} days`;
      if (operator === "lte") return `in shelter for up to ${value} days`;
      if (operator === "within_last_days") return `admitted within the last ${value} days`;
      if (operator === "older_than_days") return `in shelter for over ${value} days`;
      return null;
    }

    case "zone_code":
      if (operator === "eq") return `in kennel zone ${value}`;
      if (operator === "in" && Array.isArray(value)) return `in kennel zones ${describeValues(field, value, lookup)}`;
      return null;

    default:
      return null;
  }
}

export function buildDescriptionSuggestion(
  conditions: ConditionLeaf[],
  lookup: GlossaryLookup,
): string {
  if (!conditions || conditions.length === 0) return "";

  const speciesLeaf = conditions.find(
    (c) => c.field === "species" && (c.operator === "eq" || c.operator === "in"),
  );
  const otherConditions = conditions.filter((c) => c.field !== "species");

  let subject: string;
  if (!speciesLeaf) {
    subject = "All animals";
  } else {
    const val = speciesLeaf.value;
    const vals = Array.isArray(val) ? (val as string[]) : [String(val)];
    if (vals.length === 1) {
      subject = vals[0] === "DOG" ? "Dogs" : vals[0] === "CAT" ? "Cats" : `${vals[0].charAt(0).toUpperCase()}${vals[0].slice(1).toLowerCase()}s`;
    } else {
      subject = "Animals";
    }
  }

  const predicates = otherConditions
    .map((c) => buildConditionPhrase(c, lookup))
    .filter((p): p is string => p !== null);

  if (predicates.length === 0) return `${subject}.`;
  return `${subject} ${predicates.join(", ")}.`;
}

const GROUPBY_DIMENSION_LABELS: Record<string, string> = {
  species: "species",
  location: "location",
  kennel_status: "kennel status",
  adoption_level: "adoption level",
  zone: "kennel zone",
};

export function buildChartNarrative(
  conditions: ConditionLeaf[],
  lookup: GlossaryLookup,
  days: number,
  granularity: "daily" | "weekly",
  groupBy: string,
): string {
  const speciesLeaf = conditions.find(
    (c) => c.field === "species" && (c.operator === "eq" || c.operator === "in"),
  );
  const otherConditions = conditions.filter((c) => c.field !== "species");

  let subject: string;
  if (!speciesLeaf) {
    subject = "all animals";
  } else {
    const val = speciesLeaf.value;
    const vals = Array.isArray(val) ? (val as string[]) : [String(val)];
    if (vals.length === 1) {
      subject = vals[0] === "DOG" ? "dogs" : vals[0] === "CAT" ? "cats" : `${vals[0].toLowerCase()}s`;
    } else {
      subject = "animals";
    }
  }

  const predicates = otherConditions
    .map((c) => buildConditionPhrase(c, lookup))
    .filter((p): p is string => p !== null);

  const granularityLabel = granularity === "daily" ? "Daily" : "Weekly";
  const groupByLabel = GROUPBY_DIMENSION_LABELS[groupBy];

  let sentence = `${granularityLabel} count of ${subject}`;
  if (predicates.length > 0) sentence += ` ${predicates.join(", ")}`;
  if (groupByLabel) sentence += `, broken down by ${groupByLabel}`;
  sentence += `, over the past ${days} days.`;

  return sentence;
}
